/**
 * Первичная разметка сервера.
 *
 * Раньше это была команда в терминале, а значит — недоступная тому, у кого
 * нет доступа к машине с ботом. Разметка идемпотентна: повторный запуск
 * находит уже созданное и обновляет его, поэтому кнопку можно нажать снова.
 */

import { useState, type ReactNode } from 'react';
import { hasCapability, type SessionUser } from '@ak/shared';

import { apiPost } from '../lib/api.ts';
import type { SelectedGuild } from '../lib/guilds.ts';
import { Badge, Button, Panel } from '../components/ui.tsx';

interface LayoutResult {
  createdRoles: number;
  channels: Record<string, string>;
  movedRollbackChannels: number;
  legacyCategoryRemoved: boolean;
  warnings: string[];
}

/** Что именно появится на сервере — человек должен понимать это до нажатия. */
const WILL_CREATE = [
  'Роли: уровни игроков, модерация, доступ к разделам',
  'Категории и каналы: анкеты, наборы, заявки в семью, откаты, журнал',
  'Панели с кнопками: анкета игрока, набор, заявка в семью',
  'Привязку каналов и ролей в настройках сообщества',
];

export function SetupPage({ user, guild }: { user: SessionUser; guild: SelectedGuild }): ReactNode {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<LayoutResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canManage = hasCapability(user.level, 'settings.edit');

  const run = (): void => {
    setBusy(true);
    setError(null);

    apiPost<LayoutResult>('/api/guilds/setup')
      .then((response) => {
        setResult(response);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Не удалось разметить сервер');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Panel className="mx-auto w-full max-w-2xl p-8">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Разметка сервера</h1>
        {guild.configured ? <Badge tone="positive">Размечен</Badge> : <Badge tone="warning">Не размечен</Badge>}
      </div>
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        {guild.configured
          ? `Сервер «${guild.name}» уже готов к работе. Повторная разметка вернёт удалённые каналы и роли и обновит панели.`
          : `Чтобы бот заработал на сервере «${guild.name}», нужно создать роли, каналы и панели. Существующее не удаляется: бот ищет своё по названию и переиспользует.`}
      </p>

      <ul className="mt-5 space-y-2">
        {WILL_CREATE.map((line) => (
          <li key={line} className="flex gap-2 text-sm">
            <span aria-hidden="true" style={{ color: 'var(--accent)' }}>
              •
            </span>
            {line}
          </li>
        ))}
      </ul>

      {canManage ? (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button variant="primary" onClick={run} busy={busy}>
            {guild.configured ? 'Разметить заново' : 'Разметить сервер'}
          </Button>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Занимает до минуты: Discord ограничивает частоту создания каналов.
          </span>
        </div>
      ) : (
        <div
          className="mt-6 rounded-lg px-4 py-3 text-sm"
          style={{ background: 'var(--warning-soft)', color: 'var(--warning)' }}
        >
          Разметить сервер может только владелец сообщества или администратор Discord.
        </div>
      )}

      {error !== null && (
        <p className="mt-4 text-sm" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      {result !== null && (
        <div
          className="mt-6 rounded-lg px-4 py-4 text-sm"
          style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}
        >
          <p className="font-medium">Сервер размечен</p>
          <ul className="mt-2 space-y-1" style={{ color: 'var(--text-secondary)' }}>
            <li>Создано ролей: {result.createdRoles}</li>
            <li>Каналов в привязке: {Object.keys(result.channels).length}</li>
            {result.movedRollbackChannels > 0 && <li>Перенесено каналов откатов: {result.movedRollbackChannels}</li>}
            {result.legacyCategoryRemoved && <li>Убрана старая категория откатов</li>}
          </ul>

          {result.warnings.length > 0 && (
            <div className="mt-3">
              <p className="font-medium" style={{ color: 'var(--warning)' }}>
                Предупреждения
              </p>
              <ul className="mt-1 space-y-1" style={{ color: 'var(--warning)' }}>
                {result.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <Button
            variant="primary"
            className="mt-4"
            onClick={() => {
              // Перезагрузка, а не переход: после разметки меняются права,
              // привязка и признак «сервер настроен» — проще перечитать всё.
              window.location.assign('/');
            }}
          >
            Перейти в панель
          </Button>
        </div>
      )}
    </Panel>
  );
}
